import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import InfoIcon from '@mui/icons-material/Info';
import React from 'react';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DescriptionIcon from '@mui/icons-material/Description';
import PropTypes from 'prop-types';
import { AgyleLightBulbIcon } from '../icons/agyle';
import { startCase } from 'lodash';
import ErrorIcon from '@mui/icons-material/Error';

const useStyles = makeStyles((theme) => ({
    icon: {
        marginRight: 5,
        fontSize: 22,
        verticalAlign: 'top',
        '&.hint': {
            backgroundColor: theme.palette.background.paper,
            borderStyle: 'solid',
            borderColor: theme.palette.background.paper,
            borderRadius: '50%',
            fontSize: 40,
            padding: 5,
            color: theme.palette.background.paper,
        },
    },
    oneLine: {
        display: 'flex',
        gap: 10,
        alignItems: 'center',
    },
    main: {
        borderRadius: 5,
        padding: 6,
        margin: '10px 0px',
        fontWeight: 'bold',
        fontSize: 15,
        width: '100%',
        '&.info': {
            background: theme.palette.mode === 'light' ? '#fff' : '#3D3E40',
            color: theme.palette.mode === 'light' ? '#3f3f3f' : '#f5f5f5',
        },
        '&.warning': {
            background: '#FFC400',
            color: '#172B4D',
        },
        '&.warning-white': {
            backgroundColor: theme.palette.admissionForm.patientCardBackground,
            color: theme.palette.mode === 'light' ? '#3f3f3f' : '#FFF',
        },
        '&.tip': {
            background: '#C0B6F2',
            color: '#172B4D',
        },
        '&.hint': {
            backgroundColor: theme.palette.background.default,
            color: '#FFF',
        },
        '&.holding-pen': {
            backgroundColor: '#F4C3C6',
            color: '#70191C',
            border: '1px solid #70191C',
        },
        '&.error': {
            backgroundColor: '#e85e5e',
            color: '#FFF',
        },
    },
    noMargin: {
        margin: 0,
    },
    border: {
        borderRadius: 8,
        border: '1px solid #626466',
        // background: var(--dark-neutral-700, #3D3E40);
    },
    top: {
        padding: 7,
        borderBottom: '1px solid #626466',
        borderRight: 'none',
        '&.row': {
            borderBottom: 'none',
            borderRight: '1px solid #626466',
        },
    },
    bottom: {
        padding: 7,
    },
}));

const InformationBox = (props) => {
    const classes = useStyles();
    let icon;

    switch (props.type) {
        case 'warning':
        case 'warning-white':
            icon = <WarningAmberIcon className={clsx(classes.icon, props.type)} />;
            break;

        case 'tip':
            icon = <DescriptionIcon className={clsx(classes.icon, props.type)} />;
            break;

        case 'hint':
            icon = <AgyleLightBulbIcon className={clsx(classes.icon, props.type)} />;
            break;

        case 'error':
            icon = <ErrorIcon className={clsx(classes.icon, props.type)} />;
            break;

        case 'info':
        default:
            icon = <InfoIcon className={clsx(classes.icon, props.type)} style={props.iconStyle} />;
            break;
    }

    const getTitle = () => {
        if (props.label) return props.label;
        if (props.type === 'warning-white') return 'Warning';
        if (props.type === 'info') return 'Information';
        if (props.type === 'error') return 'Caution';
        return startCase(props.type);
    };

    return (
        <div
            id={props.id}
            className={clsx(classes.main, props.type, classes.border, props.noMargin && classes.noMargin, props.oneLine && classes.oneLine)}
            style={props.style}
        >
            <div className={clsx(classes.top, props.oneLine && 'row')} style={props.titleStyle}>
                <span style={{ marginRight: 5 }}>{icon}</span>
                <span>{getTitle()}</span>
            </div>

            <div className={classes.bottom}>{props.children}</div>
        </div>
    );
};

export default InformationBox;

InformationBox.propTypes = {
    type: PropTypes.oneOf(['info', 'warning', 'tip', 'hint', 'error', 'warning-white', 'holding-pen']).isRequired,
    noMargin: PropTypes.bool,
    label: PropTypes.string,
    id: PropTypes.string,
    oneLine: PropTypes.bool,
};

InformationBox.defaultProps = {
    noMargin: false,
    id: (Math.random() + 1).toString(36).substring(7),
    oneLine: false,
};
